// src/components/profiles/GuestUser/components/moments/GuestMomentGridItem.tsx

"use client";

import React, { useMemo, useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  ChevronLeft,
  ChevronRight,
  Edit3,
  Play,
  Trash2,
} from "lucide-react";

import { useAppDispatch } from "../../../../../redux/hooks";
import {
  deleteMomentBySlug,
  fetchMomentBySlug,
} from "../../../../../redux/ugc/momentSlice";

import type { MomentDTO } from "../../../../../types/ugc/momentTypes";
import { getMomentMediaType } from "../../../../../types/ugc/momentTypes";
import { makeMediaUrl } from "../../../../../utils/navigation/contentNavigation";
import { UGC_ENDPOINTS } from "../../../../../services/ugc/endpoint/endpoints";
import { ActionMenu, Modal } from "../../../../common";

import MediaConversionPanel from "../../../../mediaConversion/MediaConversionPanel/MediaConversionPanel";
import { useMediaConversionJobs } from "../../../../../hooks/mediaConversion/useMediaConversionJobs";

import PrivateCdnImage from "@/asset-delivery/components/PrivateCdnImage";
import type { PrivateCdnTarget } from "@/asset-delivery/components/PrivateCdnImage";
import { toCleanCdnUrl } from "@/asset-delivery/utils/toCleanCdnUrl";

import "./GuestMomentGridItem.css";

type Props = {
  moment: MomentDTO;
};

type MomentImageItem = {
  id?: string | null;
  key?: string | null;
  order?: number | null;
  is_cover?: boolean | null;
  field_name?: string | null;
  cdn_url?: string | null;
  image_url?: string | null;
  url?: string | null;
  signed_url?: string | null;
};

const buildPrivateCdnTarget = (m: MomentDTO): PrivateCdnTarget => ({
  app_label: "posts",
  model: "moment",
  object_id: (m as any)?.id,
});

const cleanValue = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
};

const getImageItemUrl = (item?: MomentImageItem | null): string | null => {
  if (!item) return null;

  return (
    toCleanCdnUrl(cleanValue(item.cdn_url)) ||
    toCleanCdnUrl(cleanValue(item.image_url)) ||
    toCleanCdnUrl(cleanValue(item.url)) ||
    toCleanCdnUrl(cleanValue(item.signed_url)) ||
    null
  );
};

const getOrderedImageItems = (moment: MomentDTO): MomentImageItem[] => {
  const raw = Array.isArray((moment as any)?.image_items)
    ? ((moment as any).image_items as MomentImageItem[])
    : [];

  return [...raw]
    .filter((item) => item && (item.id || item.field_name || getImageItemUrl(item)))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const getCoverFirstImageItems = (moment: MomentDTO): MomentImageItem[] => {
  const items = getOrderedImageItems(moment);
  if (items.length <= 1) return items;

  const coverId =
    cleanValue((moment as any)?.cover_image_id) ||
    cleanValue((moment as any)?.cover_image?.id) ||
    null;

  const cover =
    (coverId && items.find((item) => item.id === coverId)) ||
    items.find((item) => item.is_cover) ||
    items[0];

  if (!cover?.id) return items;

  return [
    ...items.filter((item) => item.id === cover.id),
    ...items.filter((item) => item.id !== cover.id),
  ];
};

const getImageItemFieldName = (item?: MomentImageItem | null) => {
  if (!item) return "image";
  if (item.field_name) return item.field_name;
  if (item.id) return `image_items:${item.id}`;
  return "image";
};

export default function GuestMomentGridItem({ moment }: Props) {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const didRefetchRef = useRef(false);

  const mediaType = getMomentMediaType(moment);
  const isVideo = mediaType === "video";
  const canManage = moment.owner?.is_me === true;
  const isConverting = moment.is_converted === false;

  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const mediaTarget = useMemo(
    () => ({
      by: "slug" as const,
      appLabel: "posts",
      model: "moment",
      slug: moment.slug,
    }),
    [moment.slug]
  );

  const { jobs: conversionJobs } = useMediaConversionJobs(mediaTarget, {
    enabled: isConverting,
    fieldName: "video",
  });

  const hasActiveConversion =
    isConverting ||
    conversionJobs.some(
      (job) => job.status === "queued" || job.status === "processing"
    );

  const href = useMemo(() => {
    if (hasActiveConversion) return "#";

    return makeMediaUrl(moment.slug, {
      endpoint: UGC_ENDPOINTS.moments.root,
      scope: "flat",
      aspect: "auto",
      usePoster: true,
    });
  }, [moment.slug, hasActiveConversion]);

  const imageItems = useMemo(() => {
    if (isVideo) return [];
    return getCoverFirstImageItems(moment);
  }, [moment, isVideo]);

  const hasImageCarousel = imageItems.length > 1;

  const activeImageItem = useMemo(() => {
    if (!imageItems.length) return null;
    return imageItems[activeImageIndex] || imageItems[0] || null;
  }, [imageItems, activeImageIndex]);

  const cleanThumb = useMemo(
    () => toCleanCdnUrl((moment as any)?.thumbnail),
    [(moment as any)?.thumbnail]
  );

  const cleanLegacyImage = useMemo(
    () => toCleanCdnUrl((moment as any)?.image),
    [(moment as any)?.image]
  );

  const activeImageSrc = useMemo(() => {
    return getImageItemUrl(activeImageItem);
  }, [activeImageItem]);

  const preferredCdnSrc = useMemo(() => {
    if (isVideo) return cleanThumb || cleanLegacyImage || null;
    return activeImageSrc || cleanLegacyImage || cleanThumb || null;
  }, [isVideo, cleanThumb, cleanLegacyImage, activeImageSrc]);

  const privateTarget = useMemo(() => buildPrivateCdnTarget(moment), [moment]);

  const warmKind = useMemo(() => {
    if (isVideo) return "thumbnail";
    return "image";
  }, [isVideo]);

  const warmFieldName = useMemo(() => {
    if (isVideo) return "thumbnail";
    return getImageItemFieldName(activeImageItem);
  }, [isVideo, activeImageItem]);

  const actionOptions = useMemo(() => {
    if (!canManage) return [];

    return [
      {
        label: "Edit",
        icon: <Edit3 size={16} />,
        onClick: () => router.push(`/moments/${moment.slug}/edit`),
      },
      {
        label: "Delete",
        icon: <Trash2 size={16} />,
        danger: true,
        onClick: () => setShowDeleteModal(true),
      },
    ];
  }, [canManage, moment.slug, router]);

  useEffect(() => {
    didRefetchRef.current = false;
    setActiveImageIndex(0);
  }, [moment.slug, (moment as any)?.cover_image_id]);

  useEffect(() => {
    if (
      isConverting &&
      conversionJobs.length > 0 &&
      conversionJobs.every(
        (job) => job.status === "done" || job.status === "failed"
      ) &&
      !didRefetchRef.current
    ) {
      didRefetchRef.current = true;
      dispatch(fetchMomentBySlug(moment.slug));
    }
  }, [isConverting, conversionJobs, dispatch, moment.slug]);

  useEffect(() => {
    if (activeImageIndex >= imageItems.length) {
      setActiveImageIndex(Math.max(imageItems.length - 1, 0));
    }
  }, [activeImageIndex, imageItems.length]);

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deleteMomentBySlug(moment.slug)).unwrap();
      setShowDeleteModal(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const goPrevious = () => {
    setActiveImageIndex((current) => Math.max(current - 1, 0));
  };

  const goNext = () => {
    setActiveImageIndex((current) =>
      Math.min(current + 1, Math.max(imageItems.length - 1, 0))
    );
  };

  const renderConverting = () => {
    return (
      <div className="guest-moment-grid-item-wrapper">
        <div className="guest-moment-grid-item guest-moment-grid-item__converting">
          <MediaConversionPanel
            target={mediaTarget}
            fieldName="video"
            compact
            title="Processing video"
            density="micro"
          />
        </div>
      </div>
    );
  };

  const renderMediaSurface = () => {
    const canRenderCdn = Boolean(preferredCdnSrc);

    return (
      <Link href={href} prefetch={false} aria-label="Open moment">
        {canRenderCdn ? (
          <PrivateCdnImage
            src={preferredCdnSrc!}
            placeholderSrc={null}
            alt=""
            className="guest-moment-grid-item__img is-loaded"
            imgClassName="guest-moment-grid-item__img is-loaded"
            target={privateTarget}
            kind={warmKind as any}
            fieldName={warmFieldName as any}
            rootMargin="900px"
            threshold={0.01}
            failOpen
            imgLoading="eager"
            fetchPriority="high"
          />
        ) : (
          <div
            className="guest-moment-grid-item__placeholder"
            aria-hidden="true"
            title="No CDN URL available for this item"
          />
        )}

        {isVideo && (
          <span
            className="guest-moment-grid-item__videoBadge"
            aria-hidden="true"
          >
            <Play size={18} />
          </span>
        )}

        {!isVideo && hasImageCarousel && (
          <div
            className="guest-moment-grid-item__controls"
            aria-label={`Photo ${activeImageIndex + 1} of ${imageItems.length}`}
          >
            <button
              type="button"
              className="guest-moment-grid-item__nav"
              aria-label="Previous photo"
              disabled={activeImageIndex <= 0}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goPrevious();
              }}
            >
              <ChevronLeft size={16} strokeWidth={2.6} aria-hidden="true" />
            </button>

            <div className="guest-moment-grid-item__dots">
              {imageItems.map((item, index) => (
                <button
                  key={item.field_name || item.id || index}
                  type="button"
                  className={
                    index === activeImageIndex
                      ? "guest-moment-grid-item__dot is-active"
                      : "guest-moment-grid-item__dot"
                  }
                  aria-label={`Show photo ${index + 1}`}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setActiveImageIndex(index);
                  }}
                />
              ))}
            </div>

            <button
              type="button"
              className="guest-moment-grid-item__nav"
              aria-label="Next photo"
              disabled={activeImageIndex >= imageItems.length - 1}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                goNext();
              }}
            >
              <ChevronRight size={16} strokeWidth={2.6} aria-hidden="true" />
            </button>
          </div>
        )}
      </Link>
    );
  };

  const renderReady = () => {
    return (
      <>
        <div className="guest-moment-grid-item-wrapper">
          {canManage && (
            <div
              className="guest-moment-grid-item__menu"
              onClick={(event) => event.stopPropagation()}
            >
              <ActionMenu
                options={actionOptions}
                position="bottom-right"
                color="secondary"
                iconSize={28}
                menuWidth="150px"
              />
            </div>
          )}

          <div className="guest-moment-grid-item">
            {renderMediaSurface()}
          </div>
        </div>

        <Modal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Delete Moment"
          message={
            <>
              <p>Are you sure you want to delete this moment?</p>
              <p>
                <strong>This action cannot be undone.</strong>
              </p>
            </>
          }
          confirmText="Delete"
          cancelText="Cancel"
          confirmVariant="danger"
          isLoading={isDeleting}
          onConfirm={handleDelete}
        />
      </>
    );
  };

  if (hasActiveConversion) return renderConverting();
  return renderReady();
}